import React from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight, Download, Award, Briefcase, GraduationCap, Rocket, Languages, ExternalLink, Camera } from 'lucide-react';
import { Reveal, WordReveal, Magnetic } from '../components/Motion';
import { summary, summary2, timeline, philosophy, skillsCloud, experience, education, certifications, skillGroups, languages, personalGallery } from '../mock/data';

const TIMELINE_ICONS = [Rocket, Briefcase, GraduationCap, Briefcase, Briefcase, Briefcase];

const Hero = () => (
  <section className="px-6 md:px-12 lg:px-20 pb-20 pt-32 relative overflow-hidden">
    <div className="blob" style={{ width: 600, height: 600, background: 'var(--accent)', top: '-20%', left: '40%', opacity: 0.10 }}/>
    <div className="max-w-[1500px] mx-auto w-full relative">
      <Reveal><span className="section-eyebrow">About</span></Reveal>
      <h1 className="h-display mt-8 text-[11vw] md:text-[7vw] leading-[0.95]">
        <WordReveal text="Amrit Raj Singh"/>
      </h1>
      <Reveal delay={0.25}>
        <p className="mt-5 font-serif-i text-2xl md:text-3xl text-accent">Product Design Manager &mdash; designing where products meet intelligence.</p>
      </Reveal>

      <div className="mt-14 grid md:grid-cols-12 gap-10 items-start">
        <Reveal delay={0.35} className="md:col-span-7">
          <p className="text-[16px] md:text-[17px] leading-[1.85] text-muted">{summary}</p>
          <p className="mt-6 text-[16px] md:text-[17px] leading-[1.85] text-muted">{summary2}</p>
           {/* Certifications */}
  <div
    className="mt-12 pt-10 border-t"
    style={{ borderColor: 'var(--line)' }}
    data-testid="about-certifications"
  >
    
    <div className="flex items-center justify-center gap-10 md:gap-14 flex-wrap">
      <img
        src="/certifications/csm.png"
        alt="Scrum Alliance — CSM Certified"
        className="h-20 md:h-24 w-auto object-contain opacity-90 hover:opacity-100 transition"
      />
      <img
        src="/certifications/google.png"
        alt="Google UX Design Certificate"
        className="h-20 md:h-24 w-auto object-contain opacity-90 hover:opacity-100 transition"
      />
      <img
        src="/certifications/iit.png"
        alt="Indian Institute of Technology Roorkee"
        className="h-20 md:h-24 w-auto object-contain opacity-90 hover:opacity-100 transition bg-white/95 rounded-full p-1"
      />
    </div>
  </div>
          <div className="mt-20 flex gap-4 flex-wrap flex items-center justify-center gap-10 md:gap-14 ">
            <Magnetic><a href="https://customer-assets.emergentagent.com/job_portfolio-showcase-2374/artifacts/9g0ckkh1_Amrit_Raj_Singh_Resume_Updated.docx" className="btn-pill solid"><Download size={14}/> Download CV</a></Magnetic>
            <Magnetic><a href="#contact" className="btn-pill">Get in touch <ArrowUpRight size={14}/></a></Magnetic>
          </div>
          
        </Reveal>
        <Reveal delay={0.45} className="md:col-span-5">
          <div className="relative aspect-[4/5] rounded-2xl overflow-hidden ring-glow card-prem">
            <img
              src="https://customer-assets.emergentagent.com/job_portfolio-showcase-2374/artifacts/17hx1lsk_liftapp%20%283%29.png"
              alt="Amrit Raj Singh"
              className="w-full h-full object-cover her"
              style={{ objectPosition: 'center 18%', objectFit: 'cover' }}
            />
            <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, transparent 45%, rgba(0,0,0,0.55))' }}/>
            <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between gap-3 text-[11px] uppercase tracking-widest" style={{ color: '#F2F1ED' }}>
              <span>Noida, India</span>
              <span className="text-accent">11+ years</span>
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const Trajectory = () => (
  <section className="px-6 md:px-12 lg:px-20 py-28" style={{ background: 'var(--bg-2)' }}>
    <div className="max-w-[1500px] mx-auto">
      <span className="section-eyebrow">Trajectory</span>
      <h2 className="h-display mt-6 text-5xl md:text-7xl">A working <span className="font-serif-i text-accent">timeline.</span></h2>
      <p className="mt-6 text-muted max-w-2xl">Eleven years across five companies &mdash; from a junior UI designer in Bengaluru to leading a design team that ships globally.</p>
      <div className="mt-20 relative">
        <div className="absolute left-3 md:left-1/2 top-0 bottom-0 w-px" style={{ background: 'var(--line)' }}/>
        <div className="flex flex-col gap-16">
          {timeline.map((t, i) => {
            const Ico = TIMELINE_ICONS[i] || Briefcase;
            const right = i % 2 === 0;
            return (
              <Reveal key={t.id} delay={i * 0.06}>
                <div className="relative grid md:grid-cols-2 gap-8 items-start">
                  <div className={right ? 'md:col-start-1 md:pr-16 md:text-right' : 'md:col-start-2 md:pl-16'}>
                    <div className="text-accent font-display text-3xl">{t.year}</div>
                    <h3 className="font-display text-2xl mt-2">{t.title}</h3>
                    <p className="text-muted text-[14px] mt-3 leading-relaxed">{t.body}</p>
                  </div>
                  <div className="absolute left-0 md:left-1/2 top-2 -translate-x-1/2 w-7 h-7 rounded-full flex items-center justify-center" style={{ background: 'var(--bg-2)', border: '1px solid var(--accent)' }}>
                    <Ico size={13} className="text-accent"/>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </div>
  </section>
);

const ExperienceBlock = () => (
  <section className="px-6 md:px-12 lg:px-20 py-28">
    <div className="max-w-[1500px] mx-auto">
      <span className="section-eyebrow">Experience</span>
      <div className="mt-6 flex items-end justify-between flex-wrap gap-6">
        <h2 className="h-display text-5xl md:text-7xl">A decade of <span className="font-serif-i text-accent">shipping.</span></h2>
        <p className="text-muted max-w-md text-[14.5px]">Five roles, hundreds of products, one consistent intent &mdash; ship work users actually enjoy.</p>
      </div>
      <div className="mt-16 flex flex-col">
        {experience.map((x) => (
          <Reveal key={x.id}>
            <div className="py-10 border-t" style={{ borderColor: 'var(--line)' }}>
              <div className="grid md:grid-cols-12 gap-8">
                <div className="md:col-span-4">
                  <div className="text-dim text-[11px] uppercase tracking-widest">{x.period}</div>
                  <h3 className="font-display text-[26px] mt-3 leading-tight">{x.company}</h3>
                  <p className="text-accent text-[13.5px] mt-2">{x.role}</p>
                  {x.website && x.website !== '#' && (
                    <a href={x.website} target="_blank" rel="noreferrer" className="link-arrow mt-5 text-[12.5px]">Visit website <span className="arrow"><ExternalLink size={12}/></span></a>
                  )}
                </div>
                <ul className="md:col-span-8 space-y-3 text-[14.5px] leading-[1.85] text-muted">
                  {x.bullets.map((b, i) => (
                    <li key={`${x.id}-b${i}`} className="flex gap-3">
                      <span className="mt-2 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: 'var(--accent)' }}></span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const PhilosophyBlock = () => (
  <section className="px-6 md:px-12 lg:px-20 py-28" style={{ background: 'var(--bg-2)' }}>
    <div className="max-w-[1500px] mx-auto grid md:grid-cols-12 gap-12 items-start">
      <Reveal className="md:col-span-5">
        <span className="section-eyebrow">Philosophy</span>
        <h2 className="h-display mt-6 text-5xl md:text-7xl">How the <span className="font-serif-i text-accent">work</span> gets made.</h2>
        <p className="mt-8 text-muted leading-relaxed">Four quiet rules. They guide every brief, every critique and every shipped pixel.</p>
      </Reveal>
      <div className="md:col-span-7 grid sm:grid-cols-2 gap-6">
        {philosophy.map((p, i) => (
          <Reveal key={p.id} delay={i * 0.06}>
            <div className="card-prem p-8">
              <div className="text-dim font-display text-sm">0{i+1}</div>
              <div className="h-display text-4xl mt-6">{p.word}</div>
              <div className="font-serif-i text-muted mt-3">{p.sub}</div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const SKILL_FLOAT_ANIMATE = { y: [0, -8, 0] };
const skillFloatTransition = (i) => ({ duration: 4 + i * 0.3, repeat: Infinity, ease: 'easeInOut' });
const skillPosition = (s) => ({ left: `${s.x}%`, top: `${s.y}%`, transform: 'translate(-50%, -50%)' });
const skillFont = (s) => ({ fontSize: `${s.size}rem` });

const SkillNode = ({ skill, index }) => (
  <motion.div
    className="absolute text-center"
    style={skillPosition(skill)}
    animate={SKILL_FLOAT_ANIMATE}
    transition={skillFloatTransition(index)}
  >
    <span className="inline-block w-2 h-2 rounded-full bg-accent mb-2"/>
    <div className="font-display" style={skillFont(skill)}>{skill.name}</div>
  </motion.div>
);

const SkillsConstellation = () => (
  <section className="px-6 md:px-12 lg:px-20 py-28">
    <div className="max-w-[1500px] mx-auto">
      <span className="section-eyebrow">Capability map</span>
      <h2 className="h-display mt-6 text-5xl md:text-7xl">A constellation, <span className="font-serif-i text-accent">not a list.</span></h2>
      <p className="mt-6 text-muted max-w-2xl">A snapshot of where I spend my time &mdash; depth in research, design systems and AI-assisted workflows, with enough engineering literacy to ship.</p>
      <div className="mt-16 relative h-[520px] rounded-2xl overflow-hidden card-prem">
        <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
          {skillsCloud.map((s, i) => skillsCloud.slice(i + 1).map((t, j) => {
            const d = Math.hypot(s.x - t.x, s.y - t.y);
            if (d > 40) return null;
            return <line key={`${s.name}-${t.name}`} x1={`${s.x}%`} y1={`${s.y}%`} x2={`${t.x}%`} y2={`${t.y}%`} stroke="rgba(199,255,85,0.25)" strokeWidth="0.5"/>;
          }))}
        </svg>
        {skillsCloud.map((s, i) => (
          <SkillNode key={s.name} skill={s} index={i} />
        ))}
      </div>

      <div className="mt-20 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {skillGroups.map((g) => (
          <Reveal key={g.id}>
            <div className="card-prem p-7 h-full">
              <h3 className="font-display text-lg">{g.title}</h3>
              <div className="mt-4 flex flex-wrap gap-2">
                {g.items.map((it) => (
                  <span key={`${g.id}-${it}`} className="text-[12px] px-3 py-1.5 rounded-full" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid var(--line)' }}>{it}</span>
                ))}
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Credentials = () => (
  <section className="px-6 md:px-12 lg:px-20 py-28" style={{ background: 'var(--bg-2)' }}>
    <div className="max-w-[1500px] mx-auto grid md:grid-cols-12 gap-12">
      <Reveal className="md:col-span-5">
        <span className="section-eyebrow">Credentials</span>
        <h2 className="h-display mt-6 text-5xl md:text-6xl">Formal <span className="font-serif-i text-accent">study.</span></h2>
        <p className="mt-6 text-muted leading-relaxed">The education and certifications that shaped how I think, work and lead.</p>
      </Reveal>
      <div className="md:col-span-7">
        <h3 className="font-display text-xl mb-6 flex items-center gap-3"><GraduationCap size={18} className="text-accent"/> Education</h3>
        <div className="grid sm:grid-cols-3 gap-4">
          {education.map((e) => (
            <Reveal key={e.id}>
              <div className="card-prem p-6 h-full">
                <div className="text-accent text-[12px] uppercase tracking-widest">{e.course}</div>
                <div className="font-display text-lg mt-3">{e.school}</div>
              </div>
            </Reveal>
          ))}
        </div>

        <h3 className="font-display text-xl mt-14 mb-6 flex items-center gap-3"><Award size={18} className="text-accent"/> Certifications</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          {certifications.map((c) => (
            <Reveal key={c.id}>
              <div className="flex items-start gap-3 p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--line)' }}>
                <span className="text-accent mt-1.5 text-xs">&#9670;</span>
                <span className="text-[13.5px] leading-snug">{c.name}</span>
              </div>
            </Reveal>
          ))}
        </div>

        <h3 className="font-display text-xl mt-14 mb-6 flex items-center gap-3"><Languages size={18} className="text-accent"/> Languages</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {languages.map((l) => (
            <div key={l.id} className="p-4 rounded-xl text-center" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--line)' }}>
              <div className="font-display">{l.name}</div>
              <div className="text-dim text-[11px] uppercase tracking-widest mt-1">{l.level}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const Gallery = () => (
  <section className="px-6 md:px-12 lg:px-20 py-28 relative overflow-hidden">
    <div className="blob" style={{ width: 500, height: 500, background: '#7E6BFF', top: '20%', right: '-10%', opacity: 0.10 }}/>
    <div className="max-w-[1500px] mx-auto relative">
      <div className="flex items-end justify-between flex-wrap gap-6">
        <div>
          <span className="section-eyebrow">Personal moments</span>
          <h2 className="h-display mt-6 text-5xl md:text-7xl">A scrapbook of <span className="font-serif-i text-accent">small joys.</span></h2>
        </div>
        <div className="flex items-center gap-2 text-dim text-[11px] uppercase tracking-widest">
          <Camera size={13}/> Captured between deadlines
        </div>
      </div>
      <p className="mt-6 text-muted max-w-2xl text-[15px] leading-relaxed">Outside of work I&apos;m a father, a rider, and someone who chases sunsets. A few moments from the road, the studio and life in between.</p>

      <div className="mt-16 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-8 gap-y-16 relative">
        {personalGallery.map((g, i) => (
          <Reveal key={g.id} delay={i * 0.04}>
            <div className="polaroid" style={{ transform: `rotate(${g.tilt}deg)` }} data-cursor="hover">
              <span className="pin" aria-hidden></span>
              <div className="img-wrap">
                <img src={g.src} alt={g.caption} loading="lazy" referrerPolicy="no-referrer"/>
              </div>
              <div className="caption">
                {g.caption}
                <span className="note">{g.note}</span>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const About = () => (
  <div className="relative">
    <Hero/>
    <Trajectory/>
    <ExperienceBlock/>
    <Gallery/>
    <PhilosophyBlock/>
    <SkillsConstellation/>
    <Credentials/>
  </div>
);

export default About;
